// Wave 20.29 — Weather panel for the Feed page.
//
// Small map centered on the user's current location with a
// live thrucast overlay (temp, wind speed + FROM-bearing, condition).
// Phase 2 will add a Mapbox raster-particle wind animation on top of
// this same map; today it renders a static wind arrow so the panel
// is useful the moment the weather service comes online.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, IconButton, Slider, Stack, Typography, CircularProgress, Button } from '@mui/material';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import CloudIcon from '@mui/icons-material/Cloud';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import NavigationIcon from '@mui/icons-material/Navigation';
import LocationSearchingIcon from '@mui/icons-material/LocationSearching';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';
import mapboxgl from 'mapbox-gl';
import { attachMinimalAttribution } from '../services/mapAttribution';
import 'mapbox-gl/dist/mapbox-gl.css';
import { colors } from '../theme';
import { fmtSpeed, fmtTemp, useUnitSystem, type UnitSystem } from '../services/useUnitSystem';
import AeroRoutePlanner from './AeroRoutePlanner';
import { Link as RouterLink } from 'react-router-dom';
import { aeroSuggestion, comfortColor, comfortHeadline, computeComfort } from '../services/comfort';
import WindParticles from './WindParticles';
import ExtendedForecast from './ExtendedForecast';

const HORIZON_HOURS = 8; // + 1 for "now" → 9 total forecasts

const MAPBOX_TOKEN =
  process.env.REACT_APP_MAPBOX_TOKEN ||
  process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ||
  'pk.eyJ1IjoiYWVyby1jaGF0IiwiYSI6ImNtY3J4bnp0MTBjeTYybm42bzVjbWtsdmkifQ.rbgswhST93lMRY-CzQZ05Q';

// Wave 20.49dh — Weather now goes through the api proxy so
// stale weather-signed tokens can't silently break the panel.
// api's @Auth validates the rider's session, then forwards to
// weather's /internal/* endpoints with WEATHER_INTERNAL_SECRET.
const API_BASE = process.env.REACT_APP_API || 'https://api.aero.chat';
const WEATHER_API = `${API_BASE}/weather`;
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

mapboxgl.accessToken = MAPBOX_TOKEN;

interface Thrucast {
  lat: number;
  lng: number;
  time: string;
  temperature_c: number | null;
  pressure_pa: number | null;
  humidity_pct: number | null;
  precip_mm_1h: number | null;
  wind_speed_ms: number | null;
  wind_from_deg: number | null;
  source_run: string | null;
}

/** Round a date to the top of its hour (in UTC). Series requests
 *  hit the server's per-hour cache and stay stable as long as the
 *  wall clock's hour hasn't ticked. */
function topOfHour(base: Date, offsetHours: number): Date {
  const d = new Date(base);
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + offsetHours);
  return d;
}

const COMPASS = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];
function bearingLabel(deg: number | null | undefined): string {
  if (deg == null) return '';
  return COMPASS[Math.round((deg % 360) / 22.5) % 16];
}
function conditionOf(t: Thrucast | null): 'clear' | 'cloudy' | 'rain' | 'snow' {
  if (!t) return 'clear';
  if ((t.precip_mm_1h ?? 0) > 0.1) {
    if ((t.temperature_c ?? 10) <= 0) return 'snow';
    return 'rain';
  }
  return (t.humidity_pct ?? 0) > 80 ? 'cloudy' : 'clear';
}

interface Props {
  /** Vertical height. Fixed value (e.g. `'calc(100vh - 120px)'`)
   *  makes the internal forecast section scroll while the map
   *  stays pinned; `'auto'` (the default) lets the whole panel
   *  grow with its content and the outer page scroll — which is
   *  what mobile-web wants. */
  height?: number | string;
}

export default function WeatherPanel({ height = 'auto' }: Props) {
  // Wave 20.49ct — When height is 'auto' the forecast section
  // shouldn't try to be its own scroll container; the outer
  // page scrolls instead. Prevents the "forecast rows hidden
  // below the bottom" bug on mobile-web where the fixed-height
  // wrapper had ~150 px of usable space for the whole forecast.
  const scrollInternally = height !== 'auto';
  const units = useUnitSystem();
  const container = useRef<HTMLDivElement | null>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const marker = useRef<mapboxgl.Marker | null>(null);
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [wxError, setWxError] = useState<string | null>(null);
  const [mapReady, setMapReady] = useState<mapboxgl.Map | null>(null);
  const [windFrames, setWindFrames] = useState<
    Array<{ url: string; bbox: [number, number, number, number] }>
  >([]);
  // Wave 20.34 — full 9-hour series (now + 1..8h). Slider index picks
  // which one drives the map's readout + wind viz; the sparklines
  // read the whole array.
  const [forecasts, setForecasts] = useState<Array<Thrucast | null>>([]);
  const [seriesAnchor, setSeriesAnchor] = useState<Date | null>(null);
  const [hourIndex, setHourIndex] = useState<number>(0);
  const [fullscreen, setFullscreen] = useState(false);
  // Wave 20.49cr — Aero route planner dialog.
  const [routePlannerOpen, setRoutePlannerOpen] = useState(false);
  const tc = forecasts[hourIndex] ?? null;

  // Ask the browser for a location once. If the user denies (or
  // there's no HTTPS + secure context), fall back to a friendly
  // prompt — a globe view isn't useful for "your weather".
  useEffect(() => {
    if (!('geolocation' in navigator)) {
      setGeoError('Location not supported by this browser.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => setGeoError(err.message || 'Location unavailable'),
      { enableHighAccuracy: false, maximumAge: 5 * 60 * 1000, timeout: 8000 },
    );
  }, []);

  // Init map after we know where to center it. mapbox-gl needs a
  // valid container size, so we recreate it if coords change (e.g.
  // user granted permission after a manual "Try again").
  useEffect(() => {
    if (!coords || !container.current) return;
    if (map.current) return;
    try {
      const m = new mapboxgl.Map({
        container: container.current,
        style: 'mapbox://styles/mapbox/dark-v11',
        center: [coords.lng, coords.lat],
        zoom: 7,
        interactive: true,
        // Wave 20.49bl — no tile crossfade on mount.
        fadeDuration: 0,
        // Attribution kept via a compact AttributionControl (Mapbox
        // TOS requires it on public maps) but the big "mapbox"
        // wordmark hidden via CSS below — the wordmark is a distinct
        // asset from the required attribution text.
        attributionControl: false,
        logoPosition: 'bottom-right',
      });
      attachMinimalAttribution(m);
      map.current = m;
      marker.current = new mapboxgl.Marker({ color: colors.brand })
        .setLngLat([coords.lng, coords.lat])
        .addTo(m);
      // Wave 20.30 — the wind particle canvas sits at zIndex 2 above
      // the map. Push the marker element above that so it stays
      // visible instead of being painted over by particles.
      const markerEl = marker.current.getElement();
      markerEl.style.zIndex = '5';
      m.once('load', () => setMapReady(m));
    } catch (e) {
      // Silent: panel still renders text-only.
    }
    return () => {
      map.current?.remove();
      map.current = null;
      marker.current = null;
      setMapReady(null);
    };
  }, [coords]);

  // Wave 20.34 — Wind PNG URL is now driven by both the viewport
  // (bbox) AND the selected slider hour. Rebuild on map move, on
  // slider drag, on hourly ingest refresh (every 10 min).
  useEffect(() => {
    if (!coords || !mapReady) return;
    const build = () => {
      const b = mapReady.getBounds();
      let w = b.getWest();
      let e = b.getEast();
      let s = b.getSouth();
      let n = b.getNorth();
      if (e - w >= 360) {
        w = -180;
        e = 180;
      } else {
        w = Math.max(-180, Math.min(180, w));
        e = Math.max(-180, Math.min(180, e));
      }
      s = Math.max(-89.9, Math.min(89.9, s));
      n = Math.max(-89.9, Math.min(89.9, n));
      const bbox = `${w.toFixed(3)},${s.toFixed(3)},${e.toFixed(3)},${n.toFixed(3)}`;
      const spanDeg = Math.max(e - w, n - s);
      const size = spanDeg > 60 ? '1024x512' : spanDeg > 20 ? '768x384' : '384x192';
      const t = topOfHour(new Date(), hourIndex);
      const url = `${WEATHER_API}/wind/png?bbox=${encodeURIComponent(bbox)}&t=${encodeURIComponent(t.toISOString())}&size=${size}`;
      setWindFrames([{ url, bbox: [w, s, e, n] }]);
    };
    build();
    const onMove = () => build();
    mapReady.on('moveend', onMove);
    const id = window.setInterval(build, 10 * 60 * 1000);
    return () => {
      mapReady.off('moveend', onMove);
      window.clearInterval(id);
    };
  }, [coords, mapReady, hourIndex]);

  // Wave 20.34 — Fetch the 9-hour thrucast series (now .. +8h) in one
  // batch request. Refreshed every 10 min so the timeline follows the
  // hourly ingest.
  useEffect(() => {
    if (!coords) return;
    let cancelled = false;
    const load = async () => {
      const jwt = localStorage.getItem(TOKEN_KEY);
      if (!jwt) {
        setWxError('Sign in to load weather.');
        return;
      }
      // Anchor is "top of current hour", so all clients hit the same
      // cached server response for identical URLs across a full clock
      // hour.
      const anchor = topOfHour(new Date(), 0);
      const points = Array.from({ length: HORIZON_HOURS + 1 }, (_, i) => ({
        lat: coords.lat,
        lng: coords.lng,
        t: topOfHour(anchor, i).toISOString(),
      }));
      try {
        const res = await fetch(`${WEATHER_API}/thrucast/batch`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${jwt}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ points }),
        });
        if (!res.ok) {
          setWxError(`Weather ${res.status}`);
          return;
        }
        const body = await res.json();
        if (cancelled) return;
        setForecasts(body.forecasts ?? []);
        setSeriesAnchor(anchor);
        setWxError(null);
      } catch (e: any) {
        if (!cancelled) setWxError(e?.message || 'Weather unavailable');
      }
    };
    load();
    const id = window.setInterval(load, 10 * 60 * 1000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [coords]);

  // Comfort scores for every hour of the horizon. Memoized so the
  // sparklines + comfort strip don't re-derive on every render.
  const comforts = useMemo(
    () =>
      forecasts.map((f) =>
        f
          ? computeComfort(
              {
                temperature_c: f.temperature_c,
                wind_speed_ms: f.wind_speed_ms,
                humidity_pct: f.humidity_pct,
                precip_mm_1h: f.precip_mm_1h,
              },
              units,
            )
          : null,
      ),
    [forecasts, units],
  );

  const cond = conditionOf(tc);
  const Icon =
    cond === 'rain'
      ? WaterDropIcon
      : cond === 'snow'
      ? AcUnitIcon
      : cond === 'cloudy'
      ? CloudIcon
      : WbSunnyIcon;

  return (
    <Box
      sx={{
        borderRadius: 3,
        border: `1px solid ${colors.border}`,
        backgroundColor: colors.surface,
        overflowX: 'hidden',
        // When we're scrolling internally (desktop sidebar), clip
        // to prevent nested scrollbars. When height is 'auto'
        // (mobile-web), let the whole panel grow.
        overflowY: scrollInternally ? 'hidden' : 'visible',
        height,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 320,
        maxWidth: '100%',
      }}
    >
      <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
        <Typography variant="overline" sx={{ color: colors.brand, letterSpacing: '0.2em' }}>
          Weather
        </Typography>
        <Typography sx={{ fontWeight: 900, fontSize: '1.05rem', lineHeight: 1.1 }}>
          Where you are
        </Typography>
      </Box>

      {/* Map / geolocation states — fixed height so the analytics
          below it always have room. Taller aspect (280 px) reads as
          a proper map instead of a strip. Hides the big mapbox
          wordmark (attribution text kept, per TOS). */}
      <Box
        sx={{
          position: 'relative',
          height: 280,
          minHeight: 220,
          backgroundColor: '#0e0e12',
          flexShrink: 0,
          '& .mapboxgl-ctrl-logo': { display: 'none !important' },
          '& .mapboxgl-ctrl-attrib': {
            fontSize: '9px !important',
            backgroundColor: 'rgba(0,0,0,0.45) !important',
            padding: '1px 4px !important',
          },
          '& .mapboxgl-ctrl-attrib a': { color: 'rgba(255,255,255,0.55) !important' },
        }}
      >
        {!coords && !geoError && (
          <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', color: colors.textMuted }}>
            <CircularProgress size={22} sx={{ color: colors.brand, mb: 1.5 }} />
            <Typography sx={{ fontSize: '0.85rem' }}>Locating…</Typography>
          </Stack>
        )}
        {geoError && (
          <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ height: '100%', px: 3, color: colors.textMuted, textAlign: 'center' }}>
            <LocationSearchingIcon sx={{ color: colors.brand, fontSize: 28 }} />
            <Typography sx={{ fontSize: '0.85rem' }}>{geoError}</Typography>
            <Button
              variant="outlined"
              size="small"
              sx={{ borderColor: colors.brand, color: colors.brand, fontWeight: 700 }}
              onClick={() => {
                setGeoError(null);
                navigator.geolocation.getCurrentPosition(
                  (pos) => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
                  (err) => setGeoError(err.message || 'Location unavailable'),
                );
              }}
            >
              Try again
            </Button>
          </Stack>
        )}
        {coords && (
          <Box sx={{ position: 'absolute', inset: 0 }}>
            <Box ref={container} sx={{ width: '100%', height: '100%' }} />
            {mapReady && windFrames.length > 0 && (
              <WindParticles key={`mini-${hourIndex}`} map={mapReady as any} frames={windFrames} loopWallSec={20} />
            )}
            <IconButton
              onClick={() => setFullscreen(true)}
              size="small"
              aria-label="Expand map"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 3,
                backgroundColor: 'rgba(15,15,20,0.85)',
                border: `1px solid ${colors.border}`,
                color: colors.brand,
                '&:hover': { backgroundColor: 'rgba(15,15,20,0.95)' },
              }}
            >
              <OpenInFullIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        )}

      </Box>

      {/* Wave 20.37 — Thrucast readout moved OUT of the map container.
          The old overlay sat on top of the map, blocking ~30 % of it
          and clipping Mapbox labels underneath. Now it lives as its
          own strip directly below the map so nothing is stacked over
          anything else. */}
      {coords && tc && !wxError && (
        <Box
          sx={{
            mx: 1.25,
            mt: 1,
            px: 1.25,
            py: 1,
            borderRadius: 2,
            backgroundColor: colors.surfaceElevated,
            border: `1px solid ${colors.border}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            flexShrink: 0,
          }}
        >
          <Icon sx={{ color: colors.brand, fontSize: 22 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontWeight: 900, fontSize: '1rem', lineHeight: 1.1, color: colors.text }}>
              {fmtTemp(tc.temperature_c, units)}
              <Typography component="span" sx={{ color: colors.textMuted, fontWeight: 600, fontSize: '0.8rem', ml: 1 }}>
                {cond}
              </Typography>
            </Typography>
            <Typography sx={{ color: colors.textMuted, fontSize: '0.75rem' }}>
              {tc.humidity_pct != null ? `${Math.round(tc.humidity_pct)}% humidity` : ''}
            </Typography>
          </Box>
          {tc.wind_speed_ms != null && (
            <Stack alignItems="center" sx={{ minWidth: 62, flexShrink: 0 }}>
              <NavigationIcon
                sx={{
                  color: colors.brand,
                  fontSize: 20,
                  transform: `rotate(${(tc.wind_from_deg ?? 0) + 180}deg)`,
                }}
              />
              <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', color: colors.text, lineHeight: 1.1, mt: 0.25 }}>
                {fmtSpeed(tc.wind_speed_ms, units)}
              </Typography>
              <Typography sx={{ color: colors.textMuted, fontSize: '0.65rem', lineHeight: 1.1 }}>
                from {bearingLabel(tc.wind_from_deg)}
              </Typography>
            </Stack>
          )}
        </Box>
      )}

      {coords && wxError && (
        <Box
          sx={{
            mx: 1.25,
            mt: 1,
            px: 1.25,
            py: 1,
            borderRadius: 2,
            backgroundColor: colors.surfaceElevated,
            border: `1px solid ${colors.border}`,
            color: colors.textMuted,
            fontSize: '0.8rem',
            textAlign: 'center',
            flexShrink: 0,
          }}
        >
          {wxError}
        </Box>
      )}

      {/* ==== Wave 20.34 — Forecast analytics ==== */}
      {coords && (
        <Box
          sx={{
            flex: scrollInternally ? 1 : 'none',
            overflowY: scrollInternally ? 'auto' : 'visible',
            overflowX: 'hidden',
            px: 1.5,
            pt: 1,
            pb: 1.5,
            // Slider marks + wide sparkline cells could push horizontally
            // on very narrow sidebars; force the flex children to shrink
            // via minWidth:0 anywhere Stack row is used below.
            minWidth: 0,
          }}
        >
          {forecasts.length > 0 ? (
            <>
              <HourSlider
                index={hourIndex}
                onChange={setHourIndex}
                anchor={seriesAnchor}
                hours={HORIZON_HOURS}
              />
              <ComfortSummary
                forecast={tc}
                hourIndex={hourIndex}
                anchor={seriesAnchor}
              />
              <ComfortStrip
                comforts={comforts}
                anchor={seriesAnchor}
                selectedIndex={hourIndex}
                onSelect={setHourIndex}
              />
              {/* Wave 20.49cr — Plan aero route CTA. Opens a
                  fullscreen dialog that fetches a headwind-
                  optimised loop from /route/plan-aero for the
                  currently-selected hour. */}
              {coords && (
                <Button
                  variant="outlined"
                  onClick={() => setRoutePlannerOpen(true)}
                  sx={{
                    mt: 1,
                    color: colors.brand,
                    borderColor: colors.brand,
                    fontWeight: 800,
                    letterSpacing: '0.05em',
                  }}
                  fullWidth
                >
                  AERO AI Route Planner · this hour
                </Button>
              )}
              <ForecastSparklines forecasts={forecasts} units={units} selectedIndex={hourIndex} />
              <ExtendedForecast coords={coords ?? null} />
            </>
          ) : (
            <ForecastLoader error={wxError} />
          )}
        </Box>
      )}

      {/* Wave 20.49cr — Route planner dialog. */}
      {coords && routePlannerOpen && (
        <AeroRoutePlanner
          open={routePlannerOpen}
          onClose={() => setRoutePlannerOpen(false)}
          coords={coords}
          timestamp={
            (() => {
              const t = topOfHour(new Date(), hourIndex);
              return t.toISOString();
            })()
          }
        />
      )}

      {/* Fullscreen wind map — mount only while open so we don't keep
          a second Mapbox instance + particle sim in memory when
          nobody's looking at it. */}
      {coords && fullscreen && (
        <FullscreenWind
          coords={coords}
          hourIndex={hourIndex}
          onHourIndex={setHourIndex}
          anchor={seriesAnchor}
          onClose={() => setFullscreen(false)}
        />
      )}
    </Box>
  );
}

/** Full-screen wind viz mode. Own Mapbox instance so pan/zoom
 *  gestures don't fight with the mini-map's, and a big slider at
 *  the bottom drives the wind time step. */
function FullscreenWind({
  coords,
  hourIndex,
  onHourIndex,
  anchor,
  onClose,
}: {
  coords: { lat: number; lng: number };
  hourIndex: number;
  onHourIndex: (i: number) => void;
  anchor: Date | null;
  onClose: () => void;
}) {
  const container = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [mapReady, setMapReady] = useState<mapboxgl.Map | null>(null);
  const [windFrames, setWindFrames] = useState<
    Array<{ url: string; bbox: [number, number, number, number] }>
  >([]);
  // Wave 20.37 — set once the WindParticles fetch + decode for the
  // current hour completes. Shown in the header so it's obvious the
  // slider drag actually pulled new data (was previously indistin-
  // guishable from a no-op because inter-hour weather can look
  // similar).
  const [loadedHour, setLoadedHour] = useState<number | null>(null);

  useEffect(() => {
    if (!container.current || mapRef.current) return;
    const el = container.current;
    const m = new mapboxgl.Map({
      container: el,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: [coords.lng, coords.lat],
      zoom: 6,
      interactive: true,
      attributionControl: false,
      logoPosition: 'bottom-right',
      // Wave 20.49bl — no tile crossfade on mount.
      fadeDuration: 0,
    });
    attachMinimalAttribution(m);
    mapRef.current = m;
    new mapboxgl.Marker({ color: colors.brand })
      .setLngLat([coords.lng, coords.lat])
      .addTo(m);
    m.once('load', () => setMapReady(m));
    const ro = new ResizeObserver(() => {
      if (mapRef.current) mapRef.current.resize();
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      m.remove();
      mapRef.current = null;
      setMapReady(null);
    };
  }, [coords]);

  useEffect(() => {
    if (!mapReady) return;
    const build = () => {
      const b = mapReady.getBounds();
      let w = b.getWest();
      let e = b.getEast();
      let s = b.getSouth();
      let n = b.getNorth();
      if (e - w >= 360) { w = -180; e = 180; }
      else {
        w = Math.max(-180, Math.min(180, w));
        e = Math.max(-180, Math.min(180, e));
      }
      s = Math.max(-89.9, Math.min(89.9, s));
      n = Math.max(-89.9, Math.min(89.9, n));
      const bbox = `${w.toFixed(3)},${s.toFixed(3)},${e.toFixed(3)},${n.toFixed(3)}`;
      const spanDeg = Math.max(e - w, n - s);
      const size = spanDeg > 60 ? '1024x512' : spanDeg > 20 ? '768x384' : '512x256';
      const t = topOfHour(new Date(), hourIndex);
      const url = `${WEATHER_API}/wind/png?bbox=${encodeURIComponent(bbox)}&t=${encodeURIComponent(t.toISOString())}&size=${size}`;
      setWindFrames([{ url, bbox: [w, s, e, n] }]);
      setLoadedHour(null);
    };
    build();
    const onMove = () => build();
    mapReady.on('moveend', onMove);
    return () => { mapReady.off('moveend', onMove); };
  }, [mapReady, hourIndex]);

  const currentLabel = useMemo(() => {
    if (!anchor) return hourIndex === 0 ? 'Now' : `+${hourIndex}h`;
    const d = new Date(anchor);
    d.setHours(d.getHours() + hourIndex);
    return hourIndex === 0 ? 'Now' : d.toLocaleTimeString(undefined, { hour: 'numeric' });
  }, [anchor, hourIndex]);

  // Wave 20.37 — Was <Dialog fullScreen>. Swapped for a plain fixed
  // overlay to eliminate Portal + Fade timing issues (Mapbox was
  // measuring the container at 0×0 before the transition finished).
  // BUT: rendering inline still lets the overlay get trapped in any
  // parent stacking context (transform/filter/will-change), which is
  // why the nav + CdA panel were painting on top of the map even at
  // z-index 2147483000. Fix: portal the overlay to document.body so
  // it escapes every ancestor stacking context. Esc-to-close via the
  // useEffect below.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        // Above MUI AppBar (1100), Modal (1300), Tooltip (1500), and any
        // in-app overlays like the CdA panel that use z-index in the
        // 10-100 range. 2147483000 leaves headroom under the CSS max.
        zIndex: 2147483000,
        backgroundColor: '#0e0e12',
        overflow: 'hidden',
        '& .mapboxgl-ctrl-logo': { display: 'none !important' },
        '& .mapboxgl-ctrl-attrib': {
          fontSize: '10px !important',
          backgroundColor: 'rgba(0,0,0,0.5) !important',
        },
        '& .mapboxgl-ctrl-attrib a': { color: 'rgba(255,255,255,0.55) !important' },
      }}
    >
      <Box sx={{ position: 'absolute', inset: 0 }}>
        <Box ref={container} sx={{ width: '100%', height: '100%' }} />
        {mapReady && windFrames.length > 0 && (
          // key={hourIndex} — remount on hour change so the particle
          // field is guaranteed to redraw from the new PNG. Without it
          // a rapid slider drag can race the async decode + leave the
          // old field cached in currentField.current.
          <WindParticles
            key={`fs-${hourIndex}`}
            map={mapReady as any}
            frames={windFrames}
            loopWallSec={20}
            onFrameChange={() => setLoadedHour(hourIndex)}
          />
        )}
      </Box>

      <IconButton
        onClick={onClose}
        aria-label="Close fullscreen map"
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 4,
          backgroundColor: 'rgba(15,15,20,0.85)',
          border: `1px solid ${colors.border}`,
          color: colors.brand,
          '&:hover': { backgroundColor: 'rgba(15,15,20,0.95)' },
        }}
      >
        <CloseIcon />
      </IconButton>

      {/* Bottom slider — 8h forecast scrubber. */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 4,
          px: 3,
          py: 2,
          background: 'linear-gradient(to top, rgba(14,14,18,0.92), rgba(14,14,18,0.65) 70%, transparent)',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Stack direction="row" alignItems="baseline" justifyContent="space-between">
          <Stack direction="row" spacing={1.25} alignItems="baseline">
            <Typography sx={{ color: colors.textMuted, fontSize: '0.72rem', letterSpacing: '0.15em', fontWeight: 800 }}>
              WIND · {currentLabel.toUpperCase()}
            </Typography>
            <Typography sx={{ color: loadedHour === hourIndex ? colors.brand : colors.textFaint, fontSize: '0.7rem', fontWeight: 700 }}>
              {loadedHour === hourIndex ? '● live' : '○ loading…'}
            </Typography>
          </Stack>
          <Typography sx={{ color: colors.textFaint, fontSize: '0.7rem' }}>
            Drag to scrub 8h forecast
          </Typography>
        </Stack>
        <Slider
          value={hourIndex}
          onChange={(_, v) => onHourIndex(Array.isArray(v) ? v[0] : v)}
          min={0}
          max={HORIZON_HOURS}
          step={1}
          marks={Array.from({ length: HORIZON_HOURS + 1 }, (_, i) => ({
            value: i,
            label: i === 0 ? 'Now' : `+${i}h`,
          }))}
          sx={{
            color: colors.brand,
            '& .MuiSlider-markLabel': { color: colors.textMuted, fontSize: '0.75rem' },
            '& .MuiSlider-markLabelActive': { color: colors.text },
          }}
        />
      </Box>
    </Box>,
    document.body,
  );
}

/** Skeleton placeholder shown while the 9-hour thrucast batch is
 *  fetching. Mirrors the eventual layout so the panel doesn't jump
 *  when data lands. */
function ForecastLoader({ error }: { error: string | null }) {
  if (error) {
    return (
      <Stack alignItems="center" spacing={1.5} sx={{ py: 4, textAlign: 'center' }}>
        <Typography sx={{ color: colors.textMuted, fontSize: '0.85rem' }}>
          {error}
        </Typography>
      </Stack>
    );
  }
  const skeletonBar = (h: number, w: string | number) => (
    <Box
      sx={{
        height: h,
        width: w,
        borderRadius: 1,
        backgroundColor: colors.border,
        opacity: 0.35,
      }}
    />
  );
  return (
    <Stack spacing={1.5} sx={{ py: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <CircularProgress size={16} sx={{ color: colors.brand }} />
        <Typography sx={{ color: colors.textMuted, fontSize: '0.75rem', letterSpacing: '0.1em' }}>
          LOADING 8H FORECAST…
        </Typography>
      </Stack>
      {/* Slider row */}
      <Stack direction="row" alignItems="center" spacing={1}>
        {skeletonBar(4, '100%')}
      </Stack>
      {/* Comfort card */}
      <Stack
        direction="row"
        spacing={1.5}
        sx={{
          p: 1.5,
          borderRadius: 2,
          border: `1px solid ${colors.border}`,
          backgroundColor: colors.surfaceElevated,
        }}
      >
        {skeletonBar(48, 60)}
        <Stack spacing={0.75} sx={{ flex: 1 }}>
          {skeletonBar(10, '40%')}
          {skeletonBar(18, '70%')}
          {skeletonBar(10, '55%')}
        </Stack>
      </Stack>
      {/* Comfort strip */}
      <Stack direction="row" spacing={0.5}>
        {Array.from({ length: 9 }).map((_, i) => (
          <Box key={i} sx={{ flex: 1 }}>{skeletonBar(22, '100%')}</Box>
        ))}
      </Stack>
      {/* Sparklines */}
      {Array.from({ length: 3 }).map((_, i) => (
        <Stack key={i} direction="row" spacing={1.25} alignItems="center">
          {skeletonBar(28, 60)}
          <Box sx={{ flex: 1 }}>{skeletonBar(14, '100%')}</Box>
          {skeletonBar(28, 50)}
        </Stack>
      ))}
    </Stack>
  );
}

// =========================================================
// Wave 20.34 — Slider / comfort / sparkline sub-components
// =========================================================

function HourSlider({
  index,
  onChange,
  anchor,
  hours,
}: {
  index: number;
  onChange: (idx: number) => void;
  anchor: Date | null;
  hours: number;
}) {
  const marks = useMemo(
    () =>
      Array.from({ length: hours + 1 }, (_, i) => {
        const label = i === 0 ? 'Now' : `+${i}h`;
        return { value: i, label };
      }),
    [hours],
  );
  const currentLabel = useMemo(() => {
    if (!anchor) return index === 0 ? 'Now' : `+${index}h`;
    const d = new Date(anchor);
    d.setHours(d.getHours() + index);
    return `${d.toLocaleTimeString(undefined, { hour: 'numeric' })}`;
  }, [anchor, index]);
  return (
    // Wave 20.37 — was mb:1.25 with the Slider using mb:-0.5, which
    // pulled the mark labels ("Now  +1h  +2h …") right up against
    // whatever sat below (usually the ComfortSummary card). Bumped
    // the outer mb and dropped the negative pull so there's real
    // breathing room between the slider and the score card.
    <Box sx={{ mb: 2.5 }}>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between">
        <Typography sx={{ fontSize: '0.7rem', color: colors.textMuted, letterSpacing: '0.1em', fontWeight: 700 }}>
          FORECAST · {currentLabel}
        </Typography>
        <Typography sx={{ fontSize: '0.65rem', color: colors.textFaint }}>
          Drag to preview
        </Typography>
      </Stack>
      <Slider
        value={index}
        onChange={(_, v) => onChange(Array.isArray(v) ? v[0] : v)}
        min={0}
        max={hours}
        step={1}
        marks={marks}
        size="small"
        sx={{
          mt: 0.25,
          color: colors.brand,
          '& .MuiSlider-markLabel': {
            color: colors.textFaint,
            fontSize: '0.65rem',
          },
          '& .MuiSlider-markLabelActive': {
            color: colors.text,
          },
          '& .MuiSlider-mark': {
            backgroundColor: colors.border,
            width: 4,
            height: 4,
            borderRadius: 2,
          },
        }}
      />
    </Box>
  );
}

function ComfortSummary({
  forecast,
  hourIndex,
  anchor,
}: {
  forecast: Thrucast | null;
  hourIndex: number;
  anchor: Date | null;
}) {
  const units = useUnitSystem();
  const input = forecast
    ? {
        temperature_c: forecast.temperature_c,
        wind_speed_ms: forecast.wind_speed_ms,
        humidity_pct: forecast.humidity_pct,
        precip_mm_1h: forecast.precip_mm_1h,
      }
    : null;
  const c = input ? computeComfort(input, units) : null;
  const color = c ? comfortColor(c.score) : colors.textMuted;
  const headline = c && input ? comfortHeadline(input, c) : '—';
  const whenDate = anchor
    ? (() => {
        const d = new Date(anchor);
        d.setHours(d.getHours() + hourIndex);
        return d;
      })()
    : null;
  const whenLabel = whenDate
    ? hourIndex === 0
      ? 'RIGHT NOW'
      : whenDate.toLocaleTimeString(undefined, { hour: 'numeric' }).toUpperCase()
    : hourIndex === 0
    ? 'RIGHT NOW'
    : `+${hourIndex}H`;
  const aeroNudge = c && input ? aeroSuggestion(input, c, whenDate) : null;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        p: 1.25,
        borderRadius: 2,
        border: `1px solid ${colors.border}`,
        backgroundColor: colors.surfaceElevated,
        mb: 1.25,
        // Long headlines shouldn't push the row wider than the panel.
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ textAlign: 'center', minWidth: 60, flexShrink: 0 }}>
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '1.7rem',
            fontWeight: 900,
            color,
            lineHeight: 1,
          }}
        >
          {c ? c.score.toFixed(1) : '—'}
        </Typography>
        <Typography sx={{ color: colors.textMuted, fontSize: '0.6rem', letterSpacing: '0.1em' }}>
          / 10
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <Typography sx={{ color: colors.textFaint, fontSize: '0.58rem', letterSpacing: '0.15em', fontWeight: 700 }}>
          {whenLabel}
        </Typography>
        <Typography
          sx={{
            color,
            fontWeight: 900,
            fontSize: '0.98rem',
            lineHeight: 1.15,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {headline}
        </Typography>
        <Typography
          sx={{
            color: colors.textMuted,
            fontSize: '0.72rem',
            mt: 0.25,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {c?.primaryReason || '—'}
        </Typography>
        {aeroNudge && (
          <RouterLink to="/session" style={{ textDecoration: 'none' }}>
            <Box
              sx={{
                mt: 0.75,
                px: 0.75,
                py: 0.25,
                borderRadius: 1,
                border: `1px solid ${colors.brand}`,
                backgroundColor: 'rgba(106,255,69,0.08)',
                display: 'inline-flex',
              }}
            >
              <Typography sx={{ color: colors.brand, fontSize: '0.68rem', fontWeight: 800 }}>
                → {aeroNudge}
              </Typography>
            </Box>
          </RouterLink>
        )}
      </Box>
    </Box>
  );
}

function ComfortStrip({
  comforts,
  anchor,
  selectedIndex,
  onSelect,
}: {
  comforts: ReturnType<typeof computeComfort>[] | Array<ReturnType<typeof computeComfort> | null>;
  anchor: Date | null;
  selectedIndex: number;
  onSelect: (idx: number) => void;
}) {
  if (comforts.length === 0) return null;
  return (
    <Box sx={{ mb: 1.25 }}>
      <Typography sx={{ fontSize: '0.6rem', color: colors.textMuted, letterSpacing: '0.15em', fontWeight: 700, mb: 0.35 }}>
        NEXT {comforts.length - 1}H · GOOD → BAD
      </Typography>
      <Stack direction="row" spacing={0.5} sx={{ height: 28 }}>
        {comforts.map((c, i) => {
          const bg = c ? comfortColor(c.score) : colors.border;
          const active = i === selectedIndex;
          const hourLabel =
            anchor
              ? (() => {
                  const d = new Date(anchor);
                  d.setHours(d.getHours() + i);
                  return d.toLocaleTimeString(undefined, { hour: 'numeric' }).replace(' ', '');
                })()
              : i === 0
              ? 'Now'
              : `+${i}h`;
          return (
            <Box
              key={i}
              onClick={() => onSelect(i)}
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-end',
                cursor: 'pointer',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: 18,
                  borderRadius: 1,
                  backgroundColor: bg,
                  opacity: c ? (active ? 1 : 0.72) : 0.3,
                  border: active ? `2px solid ${colors.text}` : `1px solid transparent`,
                  transition: 'opacity 120ms, border-color 120ms',
                }}
              />
              <Typography sx={{ mt: 0.25, fontSize: '0.55rem', color: active ? colors.text : colors.textFaint }}>
                {i === 0 ? 'Now' : hourLabel}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

function ForecastSparklines({
  forecasts,
  units,
  selectedIndex,
}: {
  forecasts: Array<Thrucast | null>;
  units: UnitSystem;
  selectedIndex: number;
}) {
  const rows: Array<{
    label: string;
    values: Array<number | null>;
    format: (v: number) => string;
    color: string;
  }> = [
    {
      label: 'TEMP',
      values: forecasts.map((f) =>
        f?.temperature_c != null
          ? units === 'imperial'
            ? (f.temperature_c * 9) / 5 + 32
            : f.temperature_c
          : null,
      ),
      format: (v) => `${Math.round(v)}°${units === 'imperial' ? 'F' : 'C'}`,
      color: '#ff9a5c',
    },
    {
      label: 'WIND',
      values: forecasts.map((f) =>
        f?.wind_speed_ms != null
          ? units === 'imperial'
            ? f.wind_speed_ms * 2.23694
            : f.wind_speed_ms * 3.6
          : null,
      ),
      format: (v) => `${v.toFixed(1)} ${units === 'imperial' ? 'mph' : 'km/h'}`,
      color: '#5ca8ff',
    },
    {
      label: 'HUMIDITY',
      values: forecasts.map((f) => f?.humidity_pct ?? null),
      format: (v) => `${Math.round(v)}%`,
      color: '#a476ff',
    },
  ];
  return (
    <Stack spacing={1.25}>
      {rows.map((r) => (
        <Sparkline key={r.label} row={r} selectedIndex={selectedIndex} />
      ))}
    </Stack>
  );
}

function Sparkline({
  row,
  selectedIndex,
}: {
  row: {
    label: string;
    values: Array<number | null>;
    format: (v: number) => string;
    color: string;
  };
  selectedIndex: number;
}) {
  const clean = row.values.map((v) => (v == null ? null : v));
  const nums = clean.filter((v): v is number => v != null);
  if (nums.length === 0) return null;
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const range = Math.max(0.1, max - min);
  const W = 100;
  const H = 20;
  const path = clean
    .map((v, i) => {
      if (v == null) return '';
      const x = (i / (clean.length - 1)) * W;
      const y = H - ((v - min) / range) * H;
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .filter(Boolean)
    .join(' ');
  const selectedVal = clean[selectedIndex];
  const selectedX = (selectedIndex / (clean.length - 1)) * W;
  const selectedY = selectedVal != null ? H - ((selectedVal - min) / range) * H : null;
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, overflow: 'hidden' }}>
      <Box sx={{ width: 58, flexShrink: 0 }}>
        <Typography
          sx={{
            color: colors.textMuted,
            fontSize: '0.55rem',
            letterSpacing: '0.12em',
            fontWeight: 700,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {row.label}
        </Typography>
        <Typography
          sx={{
            color: row.color,
            fontFamily: '"JetBrains Mono", monospace',
            fontWeight: 800,
            fontSize: '0.78rem',
            lineHeight: 1.1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {selectedVal != null ? row.format(selectedVal) : '—'}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <svg viewBox={`0 0 ${W} ${H + 2}`} width="100%" height={H + 2} preserveAspectRatio="none">
          <path d={path} fill="none" stroke={row.color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" opacity={0.55} />
          {selectedY != null && (
            <>
              <line x1={selectedX} x2={selectedX} y1={0} y2={H} stroke={colors.border} strokeWidth={0.6} />
              <circle cx={selectedX} cy={selectedY} r={2.5} fill={row.color} stroke={colors.bg} strokeWidth={0.8} />
            </>
          )}
        </svg>
      </Box>
      <Box sx={{ width: 52, flexShrink: 0, textAlign: 'right' }}>
        <Typography
          sx={{
            color: colors.textMuted,
            fontSize: '0.55rem',
            lineHeight: 1.15,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          hi {row.format(max)}
        </Typography>
        <Typography
          sx={{
            color: colors.textFaint,
            fontSize: '0.55rem',
            lineHeight: 1.15,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          lo {row.format(min)}
        </Typography>
      </Box>
    </Stack>
  );
}
